import React, { useCallback, useMemo, useState, useRef, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
	FormLabel,
	FormControl,
	FormHelperText,
	LinearProgress,
	Typography,
	Toolbar,
	Button,
	Grid
} from '@material-ui/core';
import isHotkey from 'is-hotkey';
import { Editable, withReact, Slate, ReactEditor } from 'slate-react';
import { createEditor, Transforms, Editor, Range } from 'slate';
import { withHistory } from 'slate-history';
import {
	Link,
	LooksOneOutlined,
	LooksTwoOutlined,
	Looks3Outlined,
	Looks4Outlined,
	FormatListNumbered,
	FormatListBulleted,
	FormatBold,
	FormatItalic,
	FormatUnderlined,
	Undo,
	Redo,
	Image
} from '@material-ui/icons';
import ReactS3Uploader from 'react-s3-uploader';
import clsx from 'clsx';

import _e from 'common/localeUtil';
import Leaf from './Leaf';
import Element from './Element';
import ToolbarDivider from './ToolbarDivider';
import AutoToolbarButton, { ToolbarButton, HistoryToolbarButton } from './AutoToolbarButton';
import { toggleMark, HOTKEYS, handleCompositionEnd, handleDOMBeforeInput } from './editorUtils';
import { withLinks, isLinkActive, insertLink } from './linkUtils';
import FormStatusComponent from '../form/FormStatusComponent';
import { withMentions, insertMention, MentionReducer, MENTIONS_CHECK_REGEX } from './mentionPlugin';
import { withDateMentions, DATE_MENTION_REGEXS } from './dateMentionPlugin';
import { fieldHeight, dataURLToBlob } from '../common';
import Superscript from '../icon/Superscript';
import Subscript from '../icon/Subscript';

const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

const ROW_HEIGHT = 20;
const DEFAULT_EMPTY_SCHEMA = [
	{
		type: 'paragraph',
		children: [
			{
				text: ''
			}
		]
	}
];

const useStyles = makeStyles(theme => ({
	progressIndicatorStyle: {
		position: 'absolute',
		width: '100%',
		height: '100%'
	},
	uploadImageIconStyle: {},

	toolbarContainerStyle: {
		marginLeft: -14,
		minHeight: 20,
		marginRight: -8,
		'&.top': {
			borderBottom: `1px solid ${theme.palette.text.disabled}`,
			marginBottom: 8
		},
		'&.bottom': {
			borderTop: `1px solid ${theme.palette.text.disabled}`,
			marginTop: 8
		}
	},

	editorRoot: {
		borderRadius: 0,
		border: `1px solid ${theme.palette.text.disabled}`,
		padding: '0px 8px',

		fontSize: 14,
		paddingLeft: 14,
		minHeight: fieldHeight,
		'&.flat': {
			borderRadius: 0,
			border: 0,
			borderBottom: `1px solid ${theme.palette.text.disabled}`
		},
		'&.top': {
			paddingBottom: 8
		},
		'&.bottom': {
			paddingTop: 8
		}
	},
	editorWrapperClass: {
		marginBottom: 0,
		'&.inline': {
			paddingBottom: 0
		}
	},
	suggestionModal: {
		position: 'absolute',
		zIndex: 1,
		padding: theme.spacing(0.5),
		background: 'white',
		borderRadius: theme.spacing(0.5),
		boxShadow: '0 1px 5px rgba(0,0,0,.2)'
	},
	mentionRow: {
		padding: '1px 3px',
		borderRadius: theme.spacing(0.5),
		background: 'transparent'
	},
	selectedMentionRow: {
		background: '#B4D5FF'
	}
}));

const FieldRichTextEditor = ({
	meta,
	compact,
	numRows,
	toolbarPosition,
	help,
	label,
	fullWidth,
	inline,
	placeholder,
	autoFocus,
	editorRef,
	input: { name, onChange, value: initialValue },
	toolbarConfig,
	s3Path,
	token,
	flat,
	onFileUploadProgress,
	maxImageHeight,
	maxImageWidth,
	onFilePreprocess,
	userMention,
	dateMention
}) => {
	const classes = useStyles();
	const mentionPopupRef = useRef();
	const dateMentionPopupRef = useRef();
	const [value, setValue] = useState(initialValue || DEFAULT_EMPTY_SCHEMA);
	const [mention, mentionDispatch] = useReducer(
		MentionReducer.reducer,
		MentionReducer.initialState
	);
	// console.log('RTE', toolbarConfig);
	const [selectedMention, setSelectedMention] = useState(null);

	const [progress, setProgress] = useState(0);
	const renderElement = useCallback(
		props => <Element setSelectedMention={setSelectedMention} {...props} />,
		[]
	);
	const renderLeaf = useCallback(props => <Leaf {...props} />, []);
	const editor = useMemo(() => {
		let linkedEditor = withLinks(withHistory(withReact(createEditor())));
		if (userMention.enabled) {
			linkedEditor = withMentions(linkedEditor);
		}
		if (dateMention.enabled) {
			linkedEditor = withDateMentions(linkedEditor);
		}
		return linkedEditor;
	}, [userMention.enabled, dateMention.enabled]);

	const reset = () => {
		console.log('Reset editor', DEFAULT_EMPTY_SCHEMA);
		Transforms.deselect(editor);
		setValue(DEFAULT_EMPTY_SCHEMA);
	};
	useEffect(() => {
		if (editorRef) {
			editorRef.current = reset;
		}
	}, []);
	// https://github.com/ianstormtaylor/slate/issues/3332#issuecomment-685200061
	useEffect(() => {
		if (!value) {
			Transforms.deselect(editor);
		}
	}, [value]);

	useEffect(() => {
		if (mention.target && mention.suggestions.length > 0) {
			const el = mentionPopupRef.current;
			const domRange = ReactEditor.toDOMRange(editor, mention.target);
			const rect = domRange.getBoundingClientRect();
			el.style.top = `${rect.top + window.pageYOffset + 24}px`;
			el.style.left = `${rect.left + window.pageXOffset}px`;
		}
	}, [mention.suggestions.length, editor, mention.target]);

	const handleEditorKeyDown = useCallback(
		event => {
			if (mention.target) {
				switch (event.key) {
					case 'ArrowDown':
						event.preventDefault();
						mentionDispatch({ type: MentionReducer.Actions.GO_DOWN });
						break;
					case 'ArrowUp':
						event.preventDefault();
						mentionDispatch({ type: MentionReducer.Actions.GO_UP });
						break;
					case 'Tab':
					case 'Enter':
						event.preventDefault();
						Transforms.select(editor, mention.target);
						insertMention(editor, mention.suggestions[mention.index]);
						mentionDispatch({ type: MentionReducer.Actions.CLEAR_TARGET });
						break;
					case 'Escape':
						event.preventDefault();
						mentionDispatch({ type: MentionReducer.Actions.CLEAR_TARGET });
						break;
					default:
						break;
				}
			}

			Object.keys(HOTKEYS).forEach(hotkey => {
				if (isHotkey(hotkey, event)) {
					event.preventDefault();
					const mark = HOTKEYS[hotkey];
					toggleMark(editor, mark);
				}
			});
		},
		[editor, mention.search, mention.target]
	);

	const imageUpload = useRef();
	const lastComposeData = useRef(null);

	const onDOMBeforeInput = useCallback(e => handleDOMBeforeInput(editor, lastComposeData, e), [
		editor
	]);
	const onCompositionEnd = useCallback(e => handleCompositionEnd(editor, lastComposeData, e), [
		editor
	]);

	const onSignedUrl = useCallback(response => {
		console.log(response);
	}, []);
	const onUploadError = useCallback(message => {
		console.log(message);
	}, []);
	const onUploadProgress = useCallback(
		uploadProgress => {
			onFileUploadProgress && onFileUploadProgress(uploadProgress);
		},
		[onFileUploadProgress]
	);
	const onUploadComplete = useCallback(signResult => {
		// console.log("upload complete", signResult);
		const { signedUrl } = signResult;
		setProgress(0);
		const url = signedUrl.replace(/\?.*$/, '');
		console.log(signedUrl, url);
	}, []);
	const preprocessImage = useCallback(
		(file, cb) => {
			console.log('On upload start', maxImageWidth, file, cb);
			if (maxImageWidth) {
				// Load the image
				const reader = new FileReader();
				reader.onload = readerEvent => {
					const image = new Image();
					console.log('reader loaded');
					image.onload = imageEvent => {
						// Resize the image
						console.log('image loaded', imageEvent);
						const canvas = document.createElement('canvas');
						let { width } = image;
						let { height } = image;

						height *= maxImageWidth / width;
						width = maxImageWidth;
						if (height > maxImageHeight) {
							height = maxImageHeight;
						}
						canvas.width = width;
						canvas.height = height;
						canvas.getContext('2d').drawImage(image, 0, 0, width, height);
						// console.log("after draw image");
						const dataUrl = canvas.toDataURL(file.type);
						const resizedImage = dataURLToBlob(dataUrl);
						resizedImage.lastModifiedDate = new Date();
						resizedImage.name = file.name;
						console.log('resizeed image', resizedImage);
						onFilePreprocess && onFilePreprocess(file);
						cb(resizedImage);
					};
					// console.log(readerEvent.target.result);
					image.src = readerEvent.target.result;
				};
				console.log(file);
				reader.readAsDataURL(file);
			} else {
				onFilePreprocess && onFilePreprocess(file);
				cb(file);
			}
		},
		[onFilePreprocess, maxImageWidth, maxImageHeight]
	);

	const renderToolbar = useCallback(() => {
		// console.log('toolbarconfig', toolbarConfig);
		return (
			<Toolbar
				variant="dense"
				disableGutters
				className={`${toolbarPosition}`}
				classes={{ root: classes.toolbarContainerStyle }}
			>
				{toolbarConfig.history && (
					<>
						<HistoryToolbarButton type="UNDO" IconComponent={Undo} />
						<HistoryToolbarButton type="REDO" IconComponent={Redo} />
						<ToolbarDivider />
					</>
				)}
				<AutoToolbarButton format="bold" IconComponent={FormatBold} />
				<AutoToolbarButton format="italic" IconComponent={FormatItalic} />
				<AutoToolbarButton format="underlined" IconComponent={FormatUnderlined} />

				{toolbarConfig.superscript && (
					<AutoToolbarButton format="superscript" IconComponent={Superscript} />
				)}
				{toolbarConfig.subscript && (
					<AutoToolbarButton format="subscript" IconComponent={Subscript} />
				)}

				{toolbarConfig.h1 && (
					<AutoToolbarButton format="heading-one" IconComponent={LooksOneOutlined} />
				)}
				{toolbarConfig.h2 && (
					<AutoToolbarButton format="heading-two" IconComponent={LooksTwoOutlined} />
				)}
				{toolbarConfig.h3 && (
					<AutoToolbarButton format="heading-three" IconComponent={Looks3Outlined} />
				)}
				{toolbarConfig.h4 && (
					<AutoToolbarButton format="heading-four" IconComponent={Looks4Outlined} />
				)}
				<ToolbarDivider />
				<AutoToolbarButton format="numbered-list" IconComponent={FormatListNumbered} />
				<AutoToolbarButton format="bulleted-list" IconComponent={FormatListBulleted} />

				{/* <AutoToolbarButton
					format="indent"
					IconComponent={FormatIndentIncrease}
				/>
				<AutoToolbarButton
					format="outdent"
					IconComponent={FormatIndentDecrease}
				/> */}
				{(toolbarConfig.link || toolbarConfig.image) && <ToolbarDivider />}

				{toolbarConfig.link && (
					<ToolbarButton
						active={isLinkActive(editor)}
						IconComponent={Link}
						onClick={event => {
							event.preventDefault();
							// eslint-disable-next-line no-alert
							const url = window.prompt('Enter the URL of the link:');
							if (!url) return;
							insertLink(editor, url);
						}}
					/>
				)}
				{toolbarConfig.image && (
					<ToolbarButton
						IconComponent={Image}
						onClick={event => {
							event.preventDefault();
							console.log(imageUpload.current);
							imageUpload.current.value = '';
							imageUpload.current.click();
						}}
					>
						<ReactS3Uploader
							inputRef={imageUpload}
							name={`${name}-reacts3`}
							onSignedUrl={onSignedUrl}
							onError={onUploadError}
							onProgress={onUploadProgress}
							preprocess={preprocessImage}
							onFinish={onUploadComplete}
							signingUrlQueryParams={{ s3Path }}
							signingUrl="/private/rest/upload/s3/sign/"
							accept="image/*"
							uploadRequestHeaders={{}}
							signingUrlHeaders={{ Authorization: `JWT ${token}` }}
							signingUrlWithCredentials
							contentDisposition="auto"
							style={{
								position: 'absolute',
								opacity: 0.1,
								width: 20,
								height: 20,
								cursor: 'pointer'
							}}
						/>
					</ToolbarButton>
				)}
			</Toolbar>
		);
	}, [toolbarConfig, imageUpload, token, s3Path, name]);

	const startQueryMentions = useCallback(() => {
		userMention.searchUsers(mention.search).then(users => {
			mentionDispatch({
				type: MentionReducer.Actions.UPDATE_SUGGESTIONS,
				payload: users
			});
		});
	}, [mention.search, userMention]);

	const handleOnchange = useCallback(
		val => {
			setValue(val);
			onChange(val);
			console.log(onChange, val);
			if (!userMention.enabled && !dateMention.enabled) {
				return;
			}
			const { selection } = editor;
			if (selection && Range.isCollapsed(selection)) {
				const [start] = Range.edges(selection);
				if (userMention.enabled) {
					const wordBefore = Editor.before(editor, start, { unit: 'word' });
					const before = wordBefore && Editor.before(editor, wordBefore);
					const beforeRange = before && Editor.range(editor, before, start);
					const beforeText = beforeRange && Editor.string(editor, beforeRange);
					if (beforeText) {
						const mentionBeforeMatch = beforeText.match(MENTIONS_CHECK_REGEX);
						const after = Editor.after(editor, start);
						const afterRange = Editor.range(editor, start, after);
						const afterText = Editor.string(editor, afterRange);
						const afterMatch = afterText.match(/^(\s|$)/);
						if (mentionBeforeMatch && afterMatch) {
							mentionDispatch({
								type: MentionReducer.Actions.UPDATE_SEARCH,
								payload: {
									search: mentionBeforeMatch[1],
									target: beforeRange
								}
							});
							startQueryMentions();
							return;
						}
					}
				}

				if (dateMention.enabled) {
					const wordBefore = Editor.before(editor, start, { unit: 'word', distance: 3 });
					let before = wordBefore && Editor.before(editor, wordBefore);
					before =
						before && Editor.after(editor, before, { unit: 'character', distance: 1 });
					const beforeRange = before && Editor.range(editor, before, start);
					const beforeText = beforeRange && Editor.string(editor, beforeRange);
					const after = Editor.after(editor, start);
					const afterRange = Editor.range(editor, start, after);
					const afterText = Editor.string(editor, afterRange);
					if (beforeText) {
						const cleanText = beforeText.trim();
						let dateBeforeMatch = null;
						for (let i = 0; i < DATE_MENTION_REGEXS.length; i += 1) {
							dateBeforeMatch = cleanText.match(DATE_MENTION_REGEXS[i]);
							if (dateBeforeMatch) {
								break;
							}
						}
						const afterMatch = afterText.match(/^(\s|$)/);
						if (dateBeforeMatch && afterMatch) {
							setTimeout(() => {
								Transforms.select(editor, beforeRange);
								const domRange = ReactEditor.toDOMRange(editor, beforeRange);
								const rect = domRange.getBoundingClientRect();
								const newMention = {
									date: dateBeforeMatch[0],
									rect: {
										top: rect.top,
										left: rect.left
									}
								};
								editor.insertDateMention(newMention);
								// setSelectedMention(newMention);
							}, 0);
							return;
						}
					}
				}
			}
			mentionDispatch({ type: MentionReducer.Actions.CLEAR_TARGET });
		},
		[onChange, startQueryMentions, userMention.enabled, dateMention.enabled]
	);

	const renderMentionRow = useCallback(
		(user, isSelected = false) => {
			return (
				// eslint-disable-next-line jsx-a11y/click-events-have-key-events
				<div
					key={`${user.id}`}
					className={clsx(classes.mentionRow, {
						[classes.selectedMentionRow]: isSelected
					})}
					role="button"
					tabIndex={0}
					onClick={() => {
						Transforms.select(editor, mention.target);
						insertMention(editor, user);
						mentionDispatch({ type: MentionReducer.Actions.CLEAR_TARGET });
					}}
				>
					{user.name}
				</div>
			);
		},
		[editor, mention.target]
	);

	const renderMentions = useCallback(() => {
		return (
			mention.target &&
			mention.suggestions.length > 0 && (
				<Portal>
					<div
						ref={mentionPopupRef}
						style={{
							top: '-9999px',
							left: '-9999px'
						}}
						className={classes.suggestionModal}
					>
						{mention.suggestions.map((user, i) =>
							renderMentionRow(user, i === mention.index)
						)}
					</div>
				</Portal>
			)
		);
	}, [mention.target, mention.suggestions, mention.index, renderMentionRow]);

	const renderDatePopup = useCallback(() => {
		return (
			selectedMention && (
				<Portal>
					<div
						ref={dateMentionPopupRef}
						style={{
							top: `${selectedMention.rect.top + window.pageYOffset + 24}px`,
							left: `${selectedMention.rect.left + window.pageXOffset}px`
						}}
						className={classes.suggestionModal}
					>
						<div>
							{_e('date')}: {selectedMention.date}
						</div>
						<Grid container spacing={1}>
							{dateMention.actions.map(action => (
								<Grid key={action.name} item>
									<Button
										variant="contained"
										color="primary"
										size="small"
										{...action.buttonProps}
										onClick={() => {
											action.action(selectedMention.date, task => {
												editor.updateSelectedDateMention(task);
											});
										}}
									>
										{action.name}
									</Button>
								</Grid>
							))}
						</Grid>
					</div>
				</Portal>
			)
		);
	}, [dateMention.actions, selectedMention]);

	const height = numRows * ROW_HEIGHT;
	const helpNode =
		meta.touched && (meta.error || meta.warning) ? (
			<FormStatusComponent compact={compact} error={meta.error} warning={meta.warning} />
		) : (
			help
		);

	return (
		<FormControl
			variant="outlined"
			fullWidth={fullWidth}
			className={`${label ? '' : 'no-label'} ${inline ? 'inline' : ''} ${
				classes.editorWrapperClass
			}`}
		>
			{label && (
				<FormLabel
					className="mb-1 has-text-weight-semibold"
					disabled={progress > 0}
					variant="outlined"
					error={meta.error && meta.touched}
				>
					{label}
				</FormLabel>
			)}
			<div
				className={`${classes.editorRoot} ${toolbarPosition} ${flat ? 'flat' : ''}`}
				style={{ minHeight: height }}
			>
				<Slate
					editor={editor}
					value={value}
					onChange={handleOnchange}
					autoFocus={autoFocus}
				>
					{toolbarPosition === 'top' && renderToolbar()}
					<Editable
						name={name}
						renderElement={renderElement}
						renderLeaf={renderLeaf}
						placeholder={placeholder || _e('common.editor_placeholder')}
						spellCheck
						// onBlur={handleOnBlur}
						autoFocus={autoFocus}
						disabled={progress > 0}
						onKeyDown={handleEditorKeyDown}
						onCompositionEnd={onCompositionEnd}
						onDOMBeforeInput={onDOMBeforeInput}
					/>
					{toolbarPosition === 'bottom' && renderToolbar()}
					{renderMentions()}
					{renderDatePopup()}
				</Slate>
			</div>
			<FormHelperText error={Boolean(meta.error)} variant="outlined">
				{helpNode}
			</FormHelperText>
			{progress > 0 && (
				<div className={`center-centered flex-container ${classes.progressIndicatorStyle}`}>
					<LinearProgress value={progress} variant="determinate" color="secondary" />
					<Typography variant="body3">Uploading Image...</Typography>
				</div>
			)}
		</FormControl>
	);
};

FieldRichTextEditor.propTypes = {
	editorRef: PropTypes.object,
	inline: PropTypes.bool,
	fullWidth: PropTypes.bool,
	compact: PropTypes.bool,
	autoFocus: PropTypes.bool,
	numRows: PropTypes.number,
	onFilePreprocess: PropTypes.func,
	onFileUploadProgress: PropTypes.func,
	defaultValue: PropTypes.array,
	placeholder: PropTypes.string,
	label: PropTypes.string,
	help: PropTypes.string,
	input: PropTypes.object,
	html: PropTypes.bool,
	meta: PropTypes.object,
	maxImageWidth: PropTypes.number,
	maxImageHeight: PropTypes.number,
	s3Path: PropTypes.string,
	token: PropTypes.string,
	showWordCount: PropTypes.bool,
	toolbarConfig: PropTypes.object,
	flat: PropTypes.bool,
	toolbarPosition: PropTypes.oneOf(['top', 'bottom']),
	fullHeight: PropTypes.bool,
	userMention: PropTypes.shape({
		enabled: PropTypes.bool,
		searchUsers: PropTypes.func
	}),
	dateMention: PropTypes.shape({
		enabled: PropTypes.bool,
		actions: PropTypes.arrayOf(
			PropTypes.shape({
				name: PropTypes.string.isRequired,
				action: PropTypes.func.isRequired,
				buttonProps: PropTypes.object
			})
		)
	})
};

FieldRichTextEditor.defaultProps = {
	fullWidth: true,
	toolbarPosition: 'top',
	autoFocus: false,
	numRows: 1,
	showWordCount: false,
	maxImageWidth: 500,
	maxImageHeight: 500,
	toolbarConfig: {
		h1: true,
		h2: true,
		h3: true,
		h4: true,
		history: true,
		link: true,
		image: true,
		superscript: false,
		subscript: false
	},
	fullHeight: false,
	userMention: {
		enabled: false
	},
	dateMention: {
		enabled: false,
		actions: []
	}
};
export default FieldRichTextEditor;
